import type {
  ActionOverride,
  AudienceTest,
  BaselineVersion,
  CampaignAction,
  ChallengerId,
  ChallengerVersion,
  FieldDelta,
  Json,
  OriginalDeployment,
  OriginalNode,
  VersionId
} from '../../dashboard/types/config'

export type {
  ActionOverride,
  AudienceTest,
  BaselineVersion,
  CampaignAction,
  ChallengerId,
  ChallengerVersion,
  FieldDelta,
  Json,
  OriginalDeployment,
  OriginalNode,
  VersionId
} from '../../dashboard/types/config'
import { versionLabel } from './versionLabel'

export type TestAudience = {
  id: string
  name: string
  actions: CampaignAction[]
  test?: AudienceTest
}
export type TestCampaign = { variants: TestAudience[]; goal_ids: string[]; original?: OriginalNode }
export type TestContext = { hasSplitTesting: boolean }
export type IdFactory = (prefix: string, length: number) => string
export type VersionWeights = Partial<Record<VersionId, number>>
export type StartBlocker = {
  code: 'no_goal' | 'identical_versions' | 'too_few_versions' | 'needs_pro'
  message: string
  versionId?: VersionId
}
export type AudienceVersion = { audienceId: string; versionId: VersionId; label: string }
export type LiveTestState = 'none' | 'setup' | 'pending' | 'live'

const VERSION_IDS: VersionId[] = ['A', 'B', 'C', 'D']
/** The Original page's reserved node ID. Its test covers visitors no audience personalizes on the tested elements. */
export const ORIGINAL_ID = 'original'
export const ORIGINAL_NAME = 'Original page'
const ID_CHARACTERS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'
export const UNDEPLOY_FIRST = 'Undeploy first so a new test compares against your updated website.'
export type EndOptions = { deploy?: boolean; proven?: boolean; at?: string }

const randomId: IdFactory = (prefix, length) => {
  let result = prefix
  const bytes = new Uint8Array(length * 2)
  while (result.length < prefix.length + length) {
    globalThis.crypto.getRandomValues(bytes)
    for (const byte of bytes) {
      // Reject the incomplete base62 bucket rather than biasing random IDs.
      if (byte >= 248) {
        continue
      }
      result += ID_CHARACTERS.charAt(byte % 62)
      if (result.length === prefix.length + length) {
        break
      }
    }
  }
  return result
}

export function isSafeFieldPath(path: readonly string[]): boolean {
  return (
    path.length > 0 &&
    path.every(
      (key) =>
        key.length > 0 && !/^\d+$/.test(key) && key !== '__proto__' && key !== 'constructor' && key !== 'prototype'
    )
  )
}

function isObject(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
}

function applyDelta(source: Record<string, unknown>, delta: FieldDelta, depth = 0): Record<string, unknown> {
  const key = delta.path[depth]
  if (key === undefined) {
    throw new Error('Field paths must not be empty.')
  }
  const result = { ...source }
  if (depth === delta.path.length - 1) {
    if (delta.op === 'set') {
      result[key] = delta.value
    } else {
      delete result[key]
    }
    return result
  }
  const stored = Object.hasOwn(source, key) ? source[key] : undefined
  // PHP decodes {} and [] identically, so an empty list is an empty object here too to keep both resolvers equal.
  const child = Array.isArray(stored) && stored.length === 0 ? {} : stored
  if (Array.isArray(child)) {
    throw new Error('Field paths cannot traverse arrays.')
  }
  if (delta.op === 'remove' && !isObject(child)) {
    return result
  }
  const updated = applyDelta(isObject(child) ? child : {}, delta, depth + 1)
  if (delta.op === 'remove' && Object.keys(updated).length === 0) {
    delete result[key]
  } else {
    result[key] = updated
  }
  return result
}

function withFields(action: CampaignAction, fields: FieldDelta[]): CampaignAction {
  let modifications = action.modifications ?? {}
  for (const delta of fields) {
    if (!isSafeFieldPath(delta.path)) {
      throw new Error('Field paths must contain safe object keys only.')
    }
    modifications = applyDelta(modifications, delta)
  }
  return { ...action, modifications }
}

function getVersion(audience: TestAudience, versionId: VersionId): BaselineVersion | ChallengerVersion {
  const version = audience.test?.versions.find((candidate) => candidate.id === versionId)
  if (!version) {
    throw new Error(`Unknown version ${versionId}.`)
  }
  return version
}

export function effectiveActions(audience: TestAudience, versionId: VersionId): CampaignAction[] {
  if (versionId === 'A') {
    return audience.actions
  }
  const version = getVersion(audience, versionId)
  if (version.id === 'A') {
    return audience.actions
  }
  const overrides = new Map(version.overrides.map((override) => [override.actionId, override]))
  const actions: CampaignAction[] = []
  for (const action of audience.actions) {
    const override = overrides.get(action.id)
    if (!override) {
      actions.push(action)
    } else if ('fields' in override) {
      actions.push(withFields(action, override.fields))
    }
  }
  return actions.concat(version.actions)
}

function canonical(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value.map(canonical)
  }
  if (!isObject(value)) {
    return value
  }
  return Object.fromEntries(
    Object.keys(value)
      .sort()
      .map((key) => [key, canonical(value[key])])
  )
}

export function versionsIdentical(audience: TestAudience, a: VersionId, b: VersionId): boolean {
  return (
    JSON.stringify(canonical(effectiveActions(audience, a))) ===
    JSON.stringify(canonical(effectiveActions(audience, b)))
  )
}

export function testedActionIds(audience: TestAudience): string[] {
  const ids = new Set<string>()
  for (const version of audience.test?.versions ?? []) {
    if (version.id === 'A') {
      continue
    }
    for (const override of version.overrides) {
      ids.add(override.actionId)
    }
    for (const action of version.actions) {
      ids.add(action.id)
    }
  }
  return [...ids]
}

export function audienceVersions(audience: TestAudience, displayName = audience.name): AudienceVersion[] {
  if (!audience.test) {
    return [{ audienceId: audience.id, versionId: 'A', label: displayName }]
  }
  return audience.test.versions.map((version) => ({
    audienceId: audience.id,
    versionId: version.id,
    label: `${displayName} · ${versionLabel(audience.test, version.id)}`
  }))
}

export function liveTestState(
  draft: TestAudience | undefined | null,
  live: TestAudience | undefined | null
): LiveTestState {
  if (!draft?.test) {
    return 'none'
  }
  if (draft.test.state === 'setup') {
    return 'setup'
  }
  return live?.test?.state === 'running' && live.test.id === draft.test.id && live.test.run === draft.test.run
    ? 'live'
    : 'pending'
}

/** An audience, or the Original page as a node with no base actions, so every test operation applies to both. */
export function testNode(campaign: Pick<TestCampaign, 'variants' | 'original'>, id: string): TestAudience | undefined {
  if (id !== ORIGINAL_ID) {
    return campaign.variants.find((candidate) => candidate.id === id)
  }
  const test = campaign.original?.test
  return test
    ? { id: ORIGINAL_ID, name: ORIGINAL_NAME, actions: [], test }
    : { id: ORIGINAL_ID, name: ORIGINAL_NAME, actions: [] }
}

function getAudience(campaign: TestCampaign, audienceId: string): TestAudience {
  const audience = testNode(campaign, audienceId)
  if (!audience) {
    throw new Error(`Unknown audience ${audienceId}.`)
  }
  return audience
}

/** `original` is present only while it holds a test or a deployment. */
function withOriginal<T extends TestCampaign>(campaign: T, original: OriginalNode): T {
  if (original.test || original.deployment) {
    return {
      ...campaign,
      original: {
        ...(original.test ? { test: original.test } : {}),
        ...(original.deployment ? { deployment: original.deployment } : {})
      }
    }
  }
  const next = { ...campaign }
  delete next.original
  return next
}

function updateAudience<T extends TestCampaign>(
  campaign: T,
  audienceId: string,
  update: (audience: TestAudience) => TestAudience
): T {
  const audience = getAudience(campaign, audienceId)
  const updated = update(audience)
  if (audienceId === ORIGINAL_ID) {
    if (updated.actions.length) {
      throw new Error('Control is the original page and has no changes.')
    }
    return withOriginal(campaign, { deployment: campaign.original?.deployment, test: updated.test })
  }
  return { ...campaign, variants: campaign.variants.map((candidate) => (candidate === audience ? updated : candidate)) }
}

function requireTest(audience: TestAudience): AudienceTest {
  if (!audience.test) {
    throw new Error('This audience has no test.')
  }
  return audience.test
}

function rebalance(versions: AudienceTest['versions']): AudienceTest['versions'] {
  const weight = Math.floor(100 / versions.length)
  const [baseline, ...challengers] = versions
  return [
    { ...baseline, weight: weight + (100 % versions.length) },
    ...challengers.map((version) => ({ ...version, weight }))
  ]
}

export function createTest<T extends TestCampaign>(
  campaign: T,
  audienceId: string,
  idFactory: IdFactory = randomId
): T {
  if (audienceId === ORIGINAL_ID && campaign.original?.deployment) {
    throw new Error(UNDEPLOY_FIRST)
  }
  return updateAudience(campaign, audienceId, (audience) => {
    if (audience.test) {
      throw new Error('This audience already has a test.')
    }
    const reserved = new Set(audience.actions.map((action) => action.id))
    const seen = new Set<string>()
    const actions = audience.actions.map((action) => {
      if (!seen.has(action.id)) {
        seen.add(action.id)
        return action
      }
      const id = idFactory('act_', 12)
      if (reserved.has(id)) {
        throw new Error('The ID factory produced a duplicate action ID.')
      }
      reserved.add(id)
      return { ...action, id }
    })
    return {
      ...audience,
      actions,
      test: {
        id: idFactory('abt_', 12),
        state: 'setup',
        versions: [
          { id: 'A', name: 'Version A', weight: 50 },
          { id: 'B', name: 'Version B', weight: 50, overrides: [], actions: [] }
        ]
      }
    }
  })
}

export function addVersion<T extends TestCampaign>(campaign: T, audienceId: string): T {
  return updateAudience(campaign, audienceId, (audience) => {
    const test = requireTest(audience)
    const id = VERSION_IDS.find(
      (candidate) => candidate !== 'A' && !test.versions.some((version) => version.id === candidate)
    )
    if (!id || id === 'A') {
      throw new Error('A test can have at most four versions.')
    }
    const [baseline, ...challengers] = test.versions
    challengers.push({ id, name: `Version ${id}`, weight: 1, overrides: [], actions: [] })
    challengers.sort((a, b) => a.id.localeCompare(b.id))
    return { ...audience, test: { ...test, versions: rebalance([baseline, ...challengers]) } }
  })
}

export function removeVersion<T extends TestCampaign>(campaign: T, audienceId: string, versionId: VersionId): T {
  return updateAudience(campaign, audienceId, (audience) => {
    const test = requireTest(audience)
    getVersion(audience, versionId)
    if (versionId === 'A') {
      throw new Error('Control cannot be removed.')
    }
    if (test.versions.length <= 2) {
      throw new Error('A test needs at least two versions.')
    }
    const [baseline, ...challengers] = test.versions
    return {
      ...audience,
      test: { ...test, versions: rebalance([baseline, ...challengers.filter((version) => version.id !== versionId)]) }
    }
  })
}

function updateVersion<T extends TestCampaign>(
  campaign: T,
  audienceId: string,
  versionId: VersionId,
  update: (version: BaselineVersion | ChallengerVersion, audience: TestAudience) => BaselineVersion | ChallengerVersion
): T {
  return updateAudience(campaign, audienceId, (audience) => {
    const test = requireTest(audience)
    const version = getVersion(audience, versionId)
    const updated = update(version, audience)
    const [baseline, ...challengers] = test.versions
    if (updated.id === 'A') {
      return { ...audience, test: { ...test, versions: [updated, ...challengers] } }
    }
    return {
      ...audience,
      test: {
        ...test,
        versions: [baseline, ...challengers.map((candidate) => (candidate.id === versionId ? updated : candidate))]
      }
    }
  })
}

export function renameVersion<T extends TestCampaign>(
  campaign: T,
  audienceId: string,
  versionId: VersionId,
  name: string
): T {
  return updateVersion(campaign, audienceId, versionId, (version) => ({ ...version, name }))
}

export function setWeights<T extends TestCampaign>(campaign: T, audienceId: string, weights: VersionWeights): T {
  return updateAudience(campaign, audienceId, (audience) => {
    const test = requireTest(audience)
    if (
      Object.keys(weights).length !== test.versions.length ||
      Object.keys(weights).some((id) => !test.versions.some((version) => version.id === id))
    ) {
      throw new Error('Provide a weight for every version and no other versions.')
    }
    const weighted = (version: BaselineVersion | ChallengerVersion) => {
      const weight = weights[version.id]
      if (weight === undefined || !Number.isInteger(weight) || weight < 1 || weight > 99) {
        throw new Error('Weights must be integers between 1 and 99.')
      }
      return { ...version, weight }
    }
    if (Object.values(weights).reduce((sum, weight) => sum + weight, 0) !== 100) {
      throw new Error('Version weights must sum to 100.')
    }
    const [baseline, ...challengers] = test.versions
    return {
      ...audience,
      test: {
        ...test,
        versions: [
          { ...baseline, weight: weighted(baseline).weight },
          ...challengers.map((version) => ({ ...version, weight: weighted(version).weight }))
        ]
      }
    }
  })
}

export function startBlockers(campaign: TestCampaign, audienceId: string, ctx: TestContext): StartBlocker[] {
  const audience = getAudience(campaign, audienceId)
  const blockers: StartBlocker[] = []
  if (campaign.goal_ids.length === 0) {
    blockers.push({ code: 'no_goal', message: 'Choose a campaign goal before starting this test.' })
  }
  if (!ctx.hasSplitTesting) {
    blockers.push({ code: 'needs_pro', message: 'Your plan needs split testing to start this test.' })
  }
  if (!audience.test || audience.test.versions.length < 2) {
    blockers.push({ code: 'too_few_versions', message: 'Add at least two versions before starting this test.' })
  }
  for (const version of audience.test?.versions ?? []) {
    if (version.id !== 'A' && versionsIdentical(audience, 'A', version.id)) {
      blockers.push({
        code: 'identical_versions',
        versionId: version.id,
        message: `${versionLabel(audience.test, version.id)} is identical to Control. Make a change before starting the test.`
      })
    }
  }
  return blockers
}

function beginRun<T extends TestCampaign>(
  campaign: T,
  audienceId: string,
  ctx: TestContext,
  state: AudienceTest['state'],
  idFactory: IdFactory
): T {
  const test = requireTest(getAudience(campaign, audienceId))
  if (test.state !== state) {
    throw new Error(state === 'setup' ? 'Only a setup test can be started.' : 'Only a running test can be restarted.')
  }
  const blockers = startBlockers(campaign, audienceId, ctx)
  if (blockers.length > 0) {
    throw new Error(blockers.map((blocker) => blocker.message).join(' '))
  }
  const run = idFactory('abr_', 16)
  if (test.state === 'running' && test.run === run) {
    throw new Error('Restarting requires a fresh run ID.')
  }
  return updateAudience(campaign, audienceId, (audience) => ({ ...audience, test: { ...test, state: 'running', run } }))
}

export function startTest<T extends TestCampaign>(
  campaign: T,
  audienceId: string,
  ctx: TestContext,
  idFactory: IdFactory = randomId
): T {
  return beginRun(campaign, audienceId, ctx, 'setup', idFactory)
}

export function restartTest<T extends TestCampaign>(
  campaign: T,
  audienceId: string,
  ctx: TestContext,
  idFactory: IdFactory = randomId
): T {
  return beginRun(campaign, audienceId, ctx, 'running', idFactory)
}

/**
 * An audience keeps the chosen version as its changes. The original page keeps nothing: the customer updates their
 * site, and `deploy` shows the winner to everyone who sees the original page until they Undeploy. `proven` records
 * whether the kept version's persisted verdict was Winner, and `at` is when the deployment was applied.
 */
export function endTest<T extends TestCampaign>(
  campaign: T,
  audienceId: string,
  keep: VersionId,
  options: EndOptions = {}
): T {
  if (audienceId === ORIGINAL_ID) {
    const node = getAudience(campaign, ORIGINAL_ID)
    const test = requireTest(node)
    const actions = effectiveActions(node, keep)
    if (!options.deploy) {
      return withOriginal(campaign, {})
    }
    if (keep === 'A') {
      throw new Error('Control is the original page, so there is nothing to deploy.')
    }
    const deployment: OriginalDeployment = {
      testId: test.id,
      versionId: keep,
      name: versionLabel(test, keep),
      actions,
      deployedAt: options.at ?? new Date().toISOString(),
      proven: options.proven ?? false
    }
    return withOriginal(campaign, { deployment })
  }
  if (options.deploy) {
    throw new Error(
      'Only the original page deploys a winner. Ending an audience test keeps the version as its changes.'
    )
  }
  return updateAudience(campaign, audienceId, (audience) => {
    requireTest(audience)
    const actions = effectiveActions(audience, keep)
    const updated = { ...audience, actions }
    delete updated.test
    return updated
  })
}

/** Stop showing a deployed winner, typically once the site's own content matches it. */
export function undeploy<T extends TestCampaign>(campaign: T): T {
  if (!campaign.original?.deployment) {
    throw new Error('The original page has no deployed winner.')
  }
  return withOriginal(campaign, { test: campaign.original.test })
}

const DAY_MS = 24 * 60 * 60 * 1000

/** "Deployed · 3 days" for list status chips. A deployment saved before `deployedAt` existed has no age. */
export function deployedAgeLabel(deployment: Pick<OriginalDeployment, 'deployedAt'>, now: Date = new Date()): string {
  const at = deployment.deployedAt ? Date.parse(deployment.deployedAt) : Number.NaN
  if (!Number.isFinite(at)) {
    return 'Deployed'
  }
  const days = Math.max(0, Math.floor((now.getTime() - at) / DAY_MS))
  return days === 0 ? 'Deployed · today' : `Deployed · ${days} ${days === 1 ? 'day' : 'days'}`
}

export function setFieldDelta<T extends TestCampaign>(
  campaign: T,
  audienceId: string,
  versionId: VersionId,
  actionId: string,
  delta: FieldDelta
): T {
  if (!isSafeFieldPath(delta.path)) {
    throw new Error('Field paths must contain safe object keys only.')
  }
  if (versionId === 'A') {
    return updateAudience(campaign, audienceId, (audience) => {
      if (!audience.actions.some((action) => action.id === actionId)) {
        throw new Error(`Unknown action ${actionId}.`)
      }
      return {
        ...audience,
        actions: audience.actions.map((action) => (action.id === actionId ? withFields(action, [delta]) : action))
      }
    })
  }
  return updateVersion(campaign, audienceId, versionId, (version, audience) => {
    if (version.id === 'A') {
      throw new Error('Choose a challenger version.')
    }
    const baseline = audience.actions.find((action) => action.id === actionId)
    if (!baseline) {
      if (!version.actions.some((action) => action.id === actionId)) {
        throw new Error(`Unknown action ${actionId}.`)
      }
      return {
        ...version,
        actions: version.actions.map((action) => (action.id === actionId ? withFields(action, [delta]) : action))
      }
    }
    const previous = version.overrides.find((override) => override.actionId === actionId)
    const fields = previous && 'fields' in previous ? previous.fields : []
    const path = JSON.stringify(delta.path)
    const updatedFields = [...fields.filter((field) => JSON.stringify(field.path) !== path), delta]
    withFields(baseline, updatedFields)
    const override: ActionOverride = { actionId, fields: updatedFields }
    return {
      ...version,
      overrides: previous
        ? version.overrides.map((entry) => (entry.actionId === actionId ? override : entry))
        : [...version.overrides, override]
    }
  })
}

export function clearFieldDelta<T extends TestCampaign>(
  campaign: T,
  audienceId: string,
  versionId: VersionId,
  actionId: string,
  path?: string[]
): T {
  if (path && !isSafeFieldPath(path)) {
    throw new Error('Field paths must contain safe object keys only.')
  }
  return updateVersion(campaign, audienceId, versionId, (version, audience) => {
    if (version.id === 'A' || !audience.actions.some((action) => action.id === actionId)) {
      throw new Error('Only inherited challenger actions have field deltas.')
    }
    const overrides: ActionOverride[] = []
    for (const override of version.overrides) {
      if (override.actionId !== actionId) {
        overrides.push(override)
      } else if (path && 'fields' in override) {
        const fields = override.fields.filter((field) => JSON.stringify(field.path) !== JSON.stringify(path))
        if (fields.length > 0) {
          overrides.push({ actionId, fields })
        }
      } else if (path) {
        overrides.push(override)
      }
    }
    return { ...version, overrides }
  })
}

export function unskipAction<T extends TestCampaign>(
  campaign: T,
  audienceId: string,
  versionId: VersionId,
  actionId: string
): T {
  const audience = getAudience(campaign, audienceId)
  const version = getVersion(audience, versionId)
  if (version.id === 'A' || !audience.actions.some((action) => action.id === actionId)) {
    throw new Error('Only inherited challenger actions have skip overrides.')
  }
  const override = version.overrides.find((entry) => entry.actionId === actionId)
  return override && 'skip' in override ? clearFieldDelta(campaign, audienceId, versionId, actionId) : campaign
}

export function skipAction<T extends TestCampaign>(
  campaign: T,
  audienceId: string,
  versionId: VersionId,
  actionId: string
): T {
  if (versionId === 'A') {
    return updateAudience(campaign, audienceId, (audience) => {
      if (!audience.actions.some((action) => action.id === actionId)) {
        throw new Error(`Unknown action ${actionId}.`)
      }
      const actions = audience.actions.filter((action) => action.id !== actionId)
      if (!audience.test) {
        return { ...audience, actions }
      }
      const [baseline, ...challengers] = audience.test.versions
      return {
        ...audience,
        actions,
        test: {
          ...audience.test,
          versions: [
            baseline,
            ...challengers.map((version) => ({
              ...version,
              overrides: version.overrides.filter((override) => override.actionId !== actionId)
            }))
          ]
        }
      }
    })
  }
  return updateVersion(campaign, audienceId, versionId, (version, audience) => {
    if (version.id === 'A') {
      throw new Error('Choose a challenger version.')
    }
    if (audience.actions.some((action) => action.id === actionId)) {
      return {
        ...version,
        overrides: [...version.overrides.filter((override) => override.actionId !== actionId), { actionId, skip: true }]
      }
    }
    if (!version.actions.some((action) => action.id === actionId)) {
      throw new Error(`Unknown action ${actionId}.`)
    }
    return { ...version, actions: version.actions.filter((action) => action.id !== actionId) }
  })
}
